/**
 * Power-user passphrase entry. Ported from `_legacy/mc.html` lines
 * 3470-3530 (`adminLoginModal`) — 360 px panel, top-right ✕, 42×42
 * squared icon, "RESTRICTED ACCESS" title, passphrase input with
 * show/hide eye toggle, red error text, Abort + Authenticate buttons
 * (1 : 1.4 weight, Authenticate is the primary tinted CTA).
 *
 * The Loveable v1 version this previously inherited from used a smaller
 * square modal with equal-weight buttons and no eye toggle; that's been
 * swept away in favour of legacy fidelity.
 */

import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Eye, EyeOff, ShieldCheck, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { usePowerUser } from "@/stores/usePowerUser";
import { useFocusTrap } from "@/hooks/use-focus-trap";
import { powerUser as content } from "@/content/ui";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function PowerUserModal({ open, onClose }: Props) {
  const attemptUnlock = usePowerUser((s) => s.attemptUnlock);
  const [input, setInput] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState(false);
  const focusTrapRef = useFocusTrap<HTMLDivElement>(open);

  const handleSubmit = () => {
    const ok = attemptUnlock(input.trim());
    if (ok) {
      setInput("");
      setError(false);
      setReveal(false);
      onClose();
      return;
    }
    setError(true);
    setInput("");
  };

  const handleClose = () => {
    setInput("");
    setError(false);
    setReveal(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none", maxWidth: "min(360px, calc(100vw - 24px))" }}
      >
        <DialogTitle className="sr-only">{content.modalTitle}</DialogTitle>
        <DialogDescription className="sr-only">{content.modalSubtitle}</DialogDescription>
        <div
          ref={focusTrapRef}
          className="relative overflow-hidden"
          style={{
            width: "min(360px, calc(100vw - 24px))",
            background: "var(--bg-panel)",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.4)",
            borderRadius: 14,
            boxShadow:
              "0 0 60px rgba(var(--accent-true-blue-rgb), 0.12), 0 0 120px rgba(var(--accent-true-blue-rgb), 0.06)",
          }}
        >
          {/* Top-right close */}
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="absolute z-10 transition-colors"
            style={{
              top: 10,
              right: 12,
              background: "transparent",
              border: 0,
              color: "var(--text-muted)",
              cursor: "pointer",
              padding: 4,
              lineHeight: 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--text-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--text-muted)";
            }}
          >
            <X aria-hidden style={{ width: 16, height: 16 }} />
          </button>

          {/* Header */}
          <div
            className="px-5 pb-4 pt-5"
            style={{ borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)" }}
          >
            <div className="flex items-center gap-3.5">
              <div
                aria-hidden
                className="flex flex-shrink-0 items-center justify-center"
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: "rgba(var(--accent-true-blue-rgb), 0.1)",
                  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.3)",
                  boxShadow: "0 0 14px rgba(var(--accent-true-blue-rgb), 0.15)",
                }}
              >
                <ShieldCheck
                  className="h-5 w-5"
                  style={{ color: "var(--accent-true-blue)" }}
                  aria-hidden
                />
              </div>
              <div>
                <div
                  className="font-mono font-bold tracking-[0.12em] uppercase"
                  style={{
                    color: "var(--text-primary)",
                    fontSize: "var(--fs-sub)",
                    marginBottom: 4,
                  }}
                >
                  {content.modalTitle}
                </div>
                <div
                  className="font-mono"
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "var(--fs-base)",
                    lineHeight: 1.55,
                  }}
                >
                  {content.modalSubtitle}
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 pb-5 pt-5">
            <div className="relative" style={{ marginBottom: 16 }}>
              <input
                type={reveal ? "text" : "password"}
                aria-label="Passphrase"
                autoFocus
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setError(false);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                placeholder={content.inputPlaceholder}
                className="w-full font-mono outline-none transition-all"
                style={{
                  padding: "10px 38px 10px 12px",
                  background: "rgba(var(--accent-true-blue-rgb), 0.05)",
                  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
                  borderRadius: 8,
                  color: "var(--text-primary)",
                  fontSize: "var(--fs-sub)",
                  letterSpacing: 0.5,
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "rgba(var(--accent-true-blue-rgb), 0.5)";
                  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(var(--accent-true-blue-rgb), 0.08)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "rgba(var(--accent-true-blue-rgb), 0.2)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
              <button
                type="button"
                onClick={() => setReveal((v) => !v)}
                aria-label={reveal ? "Hide passphrase" : "Show passphrase"}
                title={reveal ? "Hide" : "Show"}
                className="absolute transition-colors"
                style={{
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: 0,
                  color: "var(--text-muted)",
                  cursor: "pointer",
                  padding: 2,
                  lineHeight: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "var(--accent-true-blue)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "var(--text-muted)";
                }}
              >
                {reveal ? (
                  <EyeOff style={{ width: 14, height: 14 }} aria-hidden />
                ) : (
                  <Eye style={{ width: 14, height: 14 }} aria-hidden />
                )}
              </button>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="font-mono tracking-wider uppercase"
                  style={{
                    fontSize: "var(--fs-base)",
                    color: "var(--accent-red)",
                    textAlign: "center",
                    marginBottom: 12,
                  }}
                  role="alert"
                >
                  {content.errorMessage}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Abort + Authenticate — 1 : 1.4 weight, Authenticate primary */}
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={handleClose}
                className="font-mono font-bold tracking-[0.12em] uppercase transition-all"
                style={{
                  flex: 1,
                  padding: "9px 0",
                  background: "transparent",
                  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
                  borderRadius: 8,
                  color: "var(--text-secondary)",
                  fontSize: "var(--fs-base)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(var(--accent-true-blue-rgb), 0.45)";
                  e.currentTarget.style.color = "var(--text-primary)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(var(--accent-true-blue-rgb), 0.2)";
                  e.currentTarget.style.color = "var(--text-secondary)";
                }}
              >
                {content.cancelLabel}
              </button>
              <motion.button
                type="button"
                onClick={handleSubmit}
                whileTap={{ scale: 0.97 }}
                className="font-mono font-bold tracking-[0.12em] uppercase transition-all"
                style={{
                  flex: 1.4,
                  padding: "9px 0",
                  background: "rgba(var(--accent-true-blue-rgb), 0.1)",
                  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.45)",
                  borderRadius: 8,
                  color: "var(--accent-sky)",
                  fontSize: "var(--fs-base)",
                  cursor: "pointer",
                  boxShadow: "0 0 12px rgba(var(--accent-true-blue-rgb), 0.1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(var(--accent-true-blue-rgb), 0.18)";
                  e.currentTarget.style.boxShadow = "0 0 20px rgba(var(--accent-true-blue-rgb), 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(var(--accent-true-blue-rgb), 0.1)";
                  e.currentTarget.style.boxShadow = "0 0 12px rgba(var(--accent-true-blue-rgb), 0.1)";
                }}
              >
                {content.submitLabel}
              </motion.button>
            </div>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
